import type { ComponentTransform } from "building-types/components/componentTypes";
import type { BuildingEntityBase, DocBuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { ProfileId } from "building-types/profiles/profileTypes";
import type { Prop } from "building-types/props/propTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import type { OverhangClippingEdge } from "building-types/roofs/roofOverhangClippingTypes";
import type { RoofPerimeterRail } from "building-types/roofs/roofPerimeterTypes";
import type { GableLabel } from "building-types/roofs/roofSystemTypes";
import type {
  BellyBandBoard,
  FasciaRail,
  FasciaTrimRail,
  FriezeBoardRail,
  SoffitRail,
} from "building-types/roofs/roofTrimTypes";
import type { RoofWallSystem } from "building-types/roofs/roofWallSystemTypes";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import type { RoofFramingSettings } from "building-types/settings/roofFramingSettings";
import type { RoofElevationPlanSettings, RoofPlanSettings } from "building-types/settings/roofPlanSettings";
import type { EvaluatedRoofSettings } from "building-types/settings/roofSettings";
import type { RoofRails, RoofRails2D, RoofSide } from "building-types/settings/roofTypes";
import {
  type WallRoofBaseBoardMode,
  type WallRoofBaseBoardFilter,
  type RoofGableVents,
} from "building-types/settings/roofTypes";
import type { EaveType, GableRoofType } from "building-types/settings/settingsTypesEnum";
import type { CornerBoardConfig, ValidCornerCondition } from "building-types/trim/cornerBoardTypes";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import type { WallFaces } from "building-types/walls/wallFacesTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import type { AABB2 } from "geom/aabb2Types";
import type { Face, Extrusion } from "geom/brep";
import type { NodeBSP } from "geom/bspTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import type { Vec2 } from "geom/vec2";
import type { Vec3 } from "geom/vec3";
import type RBush from "rbush";

export interface UnderRoofTrim {
  readonly trimBase: Vec3;
  readonly trimTop: Vec3;
  readonly wallPoints: PolyLine2;
  readonly wallCenterCorner: Vec2;
  readonly levelId: string;
  readonly hasHeelHeightOffset: boolean;
  readonly cornerCondition: ValidCornerCondition;
  readonly config: CornerBoardConfig;
}

export interface RoofWallFaces extends WallFaces {
  readonly srcFace: Face | null;
  readonly edge: PolyLine2 | null;
  readonly extendedEdge: PolyLine2 | null;
  readonly side: RoofSide;
  readonly thickness: number;
  readonly addFacadeTrim: boolean;
  readonly isGapFillingWall: boolean;
}

export interface CornerBoardDatumsAABB extends AABB2 {
  readonly bottomOfSoffit: number;
  readonly bottomOfFriezeBoard: number;
  readonly levelId: string;
}

export interface GableSubRoofWallFaces {
  readonly startWallFaces: RoofWallFaces;
  readonly endWallFaces: RoofWallFaces;
  readonly vaultedRoofCapFaces: RoofWallFaces[];
  readonly hideInteriorFaces: Face[];
  readonly underRoofTrim: UnderRoofTrim[];
  readonly datumsAtCorner: RBush<CornerBoardDatumsAABB>;
}
interface GableSubroofWallEdges {
  readonly startLine?: PolyLine2;
  readonly endLine?: PolyLine2;
  readonly leftEaveLine?: PolyLine2;
  readonly rightEaveLine?: PolyLine2;
}

export interface EaveExtension {
  readonly roofTopFace: RoofFace;
  readonly rail2: PolyLine2;
  readonly rail3: PolyLine3;
  readonly exteriorWallOffsetWithFinish: number;
}

export interface GeneratedRoof extends BuildingEntityBase<"ROOF"> {
  readonly name: string;
  readonly componentTransform?: ComponentTransform;
  readonly roofSystemId: string;
  readonly index: number;
  readonly roofVersion: number;
  readonly legacyRoof: boolean;
  readonly isDormer: boolean;
  readonly isVirtualRoof: boolean;
  readonly isFloatingVirtualRoof: boolean;
  readonly polygonId: number;
  readonly levelId: string;
  readonly roofType: GableRoofType;
  readonly roofVolume: GeneratedRoofVolumeLegacy;
  readonly wallFaces: GableSubRoofWallFaces;
  readonly wallEdges: GableSubroofWallEdges;
  readonly startWindows: Table<GeneratedWindowPlacement>;
  readonly endWindows: Table<GeneratedWindowPlacement>;
  readonly leftEaveWindows?: Table<GeneratedWindowPlacement>;
  readonly rightEaveWindows?: Table<GeneratedWindowPlacement>;
  readonly gableEndLabels: GableLabel[];
  readonly props: Prop[];
  readonly wallMaterial?: ExteriorMaterial;
  readonly roofSettings: EvaluatedRoofSettings;
  readonly roofFramingSettings: RoofFramingSettings;
  readonly roofPlanSettings: RoofPlanSettings;
  readonly roofElevationPlanSettings: RoofElevationPlanSettings;
  readonly wallRoofBaseBoard: WallRoofBaseBoardMode;
  readonly wallRoofBaseBoardFilter: WallRoofBaseBoardFilter;
  readonly wallRoofBaseBoardProfile: ProfileId;
  readonly overhangClippingEdges: OverhangClippingEdge[];
  readonly suppressWallExtension: boolean;
  readonly srcRoofPerimeterRails: RoofPerimeterRail[];
  readonly roofPerimeterRails: RoofPerimeterRail[];
  readonly gableVents: RoofGableVents;
  readonly roofWallSystem?: RoofWallSystem;
  readonly friezeBoardRails: FriezeBoardRail[];
  readonly fasciaRails: FasciaRail[];
  readonly fasciaTopTrimRails: FasciaTrimRail[];
  readonly bellyBandBoardRails: BellyBandBoard[];
  readonly rails2D: RoofRails2D;
  readonly rails: RoofRails;
  readonly friezeBoards: FaceArrayViewEntity[];
  readonly fasciaBoards: FaceArrayViewEntity[];
  readonly fasciaTopTrims: FaceArrayViewEntity[];
  readonly soffits: GeneratedRoofSoffit[];
  readonly soffitRails: Table<SoffitRail[]>;
  readonly bellyBandBoards: FaceArrayViewEntity[];
  readonly wallBaseBoards: FaceArrayViewEntity[];
  readonly exposedRafters: FaceArrayViewEntity[];
  readonly gableVentFaces: FaceArrayViewEntity[];
  readonly trimsRoofMat: FaceArrayViewEntity[];
  readonly extrudedTrimsRoofMat: FaceArrayViewEntity[];
  readonly roofTopFaces: RoofFace[];
  readonly bottomFaces: Face[];
  readonly ridgeVentFaces: Face[];
  readonly ridgeCapFaces: Face[];
  readonly roofTopPlates: Extrusion[];
  readonly roofingMaterialFaces: Face[];
  readonly wallBaseBoardRails: PolyLine3[];
  /** Generation-only: present when this roof was generated from a component instance copy. */
  readonly componentInstanceChain?: readonly string[];
}

export interface GeneratedRoofSystem extends DocBuildingEntityBase<"ROOF_SYSTEM"> {
  readonly name: string;
  readonly levelId: string;
  readonly roofs: Table<GeneratedRoof>;
  readonly roofVolumes: Table<GeneratedRoofVolumeLegacy>;
  readonly blockIdToSubRoofId: Table<string>;
  readonly clipContext: MultiGableRoofClipContext;
  readonly halfStoryDiscardBSPs?: Table<NodeBSP>;
}

export interface GeneratedRoofSoffit extends BuildingEntityBase<"ROOF_SOFFIT">, FaceArrayViewEntity {
  readonly type: "ROOF_SOFFIT";
  readonly eaveType: EaveType;
  readonly direction?: Vec3;
}

export const ROOF_WALL_FACE_TAG = { type: "CONDITION", condition: "ROOF_WALL_FACE" };
